import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ColumnDefinition } from '@hub4b/models/server/column-definition';
import { LockedType } from '@hub4b/models/server/enums/locked-type';
import { Rights } from '@hub4b/models/server/enums/rights';
import { Folder } from '@hub4b/models/server/folder';
import { View } from '@hub4b/models/server/view';
import { ConnectedUsersStateService } from '@hub4b/services/state/connected-users-state.service';
import { ContextualActionType } from './models/contextual-action-type';
import { ContextualMenuOpenEvent } from './models/contextual-menu-open-event';
import { ContextualMenuSelectEvent } from './models/contextual-menu-select-event';

@Component({
    selector: 'app-table-action-cell',
    templateUrl: './table-action-cell.component.html',
    styleUrls: ['./table-action-cell.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatMenuModule, MatIconModule, MatTooltipModule, NgClass]
})
export class TableActionCellComponent {
    @Input() set view(value: View) {
        if (value) {
            this.archiveRight = this.connectedUserStateService.hasRight(Rights.ArchiveCreate) && value.archivingEnabled === true;
            this.isArchiveView = value.isArchive;
        }
    }

    /** Définition de la colonne Action. */
    @Input()
    actionColumnDefinition: ColumnDefinition;

    @Output()
    contextualMenuOpen: EventEmitter<ContextualMenuOpenEvent> = new EventEmitter<ContextualMenuOpenEvent>();

    @Output()
    contextualMenuSelect: EventEmitter<ContextualMenuSelectEvent> = new EventEmitter<ContextualMenuSelectEvent>();

    private _row: Folder;

    /** Instance du dossier affiché sur cette ligne. */
    @Input() set row(value: Folder) {
        this._row = value;
        // Affecte la valeur du droit de déverrouiller un dossier en fonction du dossier
        this.setUnlockFolderRight(value);
    }

    get row(): Folder {
        return this._row;
    }

    /** Index de la ligne. */
    @Input()
    index: number;

    @Input()
    isFollowedFolders: boolean = false;

    public lockedType = LockedType;

    public folderDeletionRight: boolean = false;

    public folderConsultationRight: boolean = false;

    public moveFolderToBoxRight: boolean = false;

    public mergeFoldersRight: boolean = false;

    public duplicateFoldersRight: boolean = false;

    public toggleReadStateRight: boolean = false;

    public addFileToFolderRight: boolean = false;

    public standByRight: boolean = false;

    public archiveRight: boolean = false;

    public unlockFolderRight: boolean = false;

    public followFolderRight: boolean = false;

    public connectedUserDisplayName: string;

    public isArchiveView: boolean = false;

    constructor(private connectedUserStateService: ConnectedUsersStateService) {
        this.folderDeletionRight = this.connectedUserStateService.hasRight(Rights.FolderDelete);
        this.folderConsultationRight = this.connectedUserStateService.hasRight(Rights.FolderRead);
        this.moveFolderToBoxRight = this.connectedUserStateService.hasRight(Rights.MoveFolderToBox);
        this.mergeFoldersRight = this.connectedUserStateService.hasRight(Rights.MergeFolder);
        this.duplicateFoldersRight = this.connectedUserStateService.hasRight(Rights.DuplicateFolder);
        this.toggleReadStateRight = this.connectedUserStateService.hasRight(Rights.toggleReadState);
        this.addFileToFolderRight = this.connectedUserStateService.hasRight(Rights.AddFileToFolder);
        this.standByRight = this.connectedUserStateService.hasRight(Rights.ChangeStandByState);
        this.followFolderRight = this.connectedUserStateService.hasRight(Rights.FollowFolder);
    }

    deleteFolder() {
        this.contextualMenuSelect.emit(new ContextualMenuSelectEvent(ContextualActionType.deleteFolder));
    }

    addFile() {
        this.contextualMenuSelect.emit(new ContextualMenuSelectEvent(ContextualActionType.addFile));
    }

    moveToBox() {
        this.contextualMenuSelect.emit(new ContextualMenuSelectEvent(ContextualActionType.moveToBox));
    }

    mergeFile() {
        this.contextualMenuSelect.emit(new ContextualMenuSelectEvent(ContextualActionType.merge));
    }

    duplicateFolder() {
        this.contextualMenuSelect.emit(new ContextualMenuSelectEvent(ContextualActionType.duplicate));
    }

    toggleReadState(state: boolean) {
        if (state === true) {
            this.contextualMenuSelect.emit(new ContextualMenuSelectEvent(ContextualActionType.markAsRead));
        } else {
            this.contextualMenuSelect.emit(new ContextualMenuSelectEvent(ContextualActionType.markAsUnread));
        }
    }

    displayFolder() {
        this.contextualMenuSelect.emit(new ContextualMenuSelectEvent(ContextualActionType.displayFolder));
    }

    changeStandByState(state: boolean) {
        if (state === true) {
            this.contextualMenuSelect.emit(new ContextualMenuSelectEvent(ContextualActionType.setInStandByState));
        } else {
            this.contextualMenuSelect.emit(new ContextualMenuSelectEvent(ContextualActionType.removeFromStandByState));
        }
    }

    copyTrackingLink() {
        this.contextualMenuSelect.emit(new ContextualMenuSelectEvent(ContextualActionType.CopyTrackingLink));
    }

    unlockFolder() {
        this.contextualMenuSelect.emit(new ContextualMenuSelectEvent(ContextualActionType.unlockFolder));
    }

    archive() {
        this.contextualMenuSelect.emit(new ContextualMenuSelectEvent(ContextualActionType.archive));
    }

    setInWaitingArchivingState() {
        this.contextualMenuSelect.emit(new ContextualMenuSelectEvent(ContextualActionType.SetInWaitingArchivingState));
    }

    removeFromWaitingArchivingState() {
        this.contextualMenuSelect.emit(new ContextualMenuSelectEvent(ContextualActionType.RemoveFromWaitingArchivingState));
    }

    followFolder(state: boolean) {
        if (state === true) {
            this.contextualMenuSelect.emit(new ContextualMenuSelectEvent(ContextualActionType.followFolder));
        } else {
            this.contextualMenuSelect.emit(new ContextualMenuSelectEvent(ContextualActionType.stopFollowFolder));
        }
    }

    /** Affecte la valeur du droit de déverrouiller un dossier */
    private setUnlockFolderRight(folder: Folder) {
        this.unlockFolderRight =
            this.connectedUserStateService.hasRight(Rights.UnlockFolder) &&
            (folder.lockedBy ? folder.lockedBy !== this.connectedUserStateService.getUserConnected().displayName : false);
    }

    /**
     * Levé lorsque l'utilisateur clique sur le menu contextuel.
     * @param event : Evenment clic de la souris
     * @param index : Index de l'élément cliqué
     */
    public onContextualMenuClick(event: MouseEvent, index: number): void {
        this.contextualMenuOpen.emit(new ContextualMenuOpenEvent(event, index));
    }
}
